import { useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  Alert,
  Button,
  Checkbox,
  DataTable,
  InputText,
  SectionCard,
  Select,
  type ColumnDef,
  type SelectOption,
} from "@/deps/ui-library";
import {
  useAccounts,
  useBankAccounts,
  useBankReconciliation,
  useCreateStatementEntry,
  useMatchCandidates,
  useMatchStatementLine,
  useMatchStatementLineToSeveral,
  useReconciliationLocks,
  useReopenReconciliation,
  useStatementMatches,
  useUnmatchStatementLine,
} from "@/api/queries";
import type { Id, LedgerMovement, MatchedStatementLine, StatementLineToReview } from "@/api/types";
import { useCan } from "@/app/session";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Dialog } from "@/components/Dialog";
import { KpiStrip } from "@/components/KpiStrip";
import { PageHeader } from "@/components/PageHeader";
import { PermittedAction } from "@/components/PermittedAction";
import { SegmentControl } from "@/components/SegmentControl";
import { TYPE_LABELS } from "@/features/parties/PartyLedger";
import { formatDate, today } from "@/lib/dates";
import { formatAmount } from "@/lib/money";
import { CreateEntryDialog, entryAccountOptions } from "./CreateEntryDialog";
import { FinishReconciliationDialog } from "./FinishReconciliationDialog";
import { ImportStatementDialog } from "./ImportStatementDialog";
import { suggestMatches } from "./suggest";

type Tab = "review" | "matched" | "books";

function pickedValue(details: { value: SelectOption | SelectOption[] | null }): string {
  const option = Array.isArray(details.value) ? details.value[0] : details.value;
  return option ? String(option.value) : "";
}

const documentLabel = (type: string, number: string) =>
  `${number} · ${TYPE_LABELS[type] ?? type.replaceAll("_", " ")}`;

const paisa = (value: string) => Math.round(Number(value) * 100);

/** A line's match, with each document it's matched to. Any of its ids undoes the whole of it. */
type MatchedLine = MatchedStatementLine & { documents: string[] };

function byLine(matches: MatchedStatementLine[]): MatchedLine[] {
  const lines = new Map<string, MatchedLine>();
  for (const match of matches) {
    const document = `${documentLabel(match.documentType, match.number)} · ${formatDate(match.postingDate)}`;
    const line = lines.get(match.lineId);
    if (line) line.documents.push(document);
    else lines.set(match.lineId, { ...match, documents: [document] });
  }
  return [...lines.values()];
}

/**
 * The bank's lines against our own, one account at a time.
 *
 * Nothing is matched by itself. A line gets a suggestion, and a person accepts it, because two payments
 * of one amount on one day can be two different customers. Matching posts nothing, so it can be undone.
 */
export function ReconciliationPage() {
  const search = useSearch({ from: "/banking/reconciliation" });
  const navigate = useNavigate();
  const can = useCan();
  const manages = can("banking.reconciliation.manage");
  const { data: accounts } = useBankAccounts();
  const accountId = search.account ?? accounts?.[0]?.id;
  const asAt = search.asAt ?? today();
  const account = accounts?.find((a) => a.id === accountId);

  const { data, error } = useBankReconciliation(accountId, asAt);
  const { data: matches } = useStatementMatches(accountId, asAt);
  const match = useMatchStatementLine();
  const unmatch = useUnmatchStatementLine();

  const [tab, setTab] = useState<Tab>("review");
  const [importing, setImporting] = useState(false);
  const [finding, setFinding] = useState<StatementLineToReview | null>(null);
  const [creating, setCreating] = useState<StatementLineToReview | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const { data: locks } = useReconciliationLocks(accountId);
  const reopen = useReopenReconciliation();
  const standing = locks?.find((lock) => lock.reopenedAt === null);
  const lockedTo = standing?.asAt;

  const suggestions = data ? suggestMatches(data) : new Map<string, LedgerMovement>();
  const failure = match.error ?? unmatch.error ?? reopen.error ?? error;
  const canCreate =
    can("accounting.journals.create") ||
    can("sales.receipts.create") ||
    can("purchases.payments.create");

  const set = (patch: { account?: string; asAt?: string }) =>
    void navigate({
      to: "/banking/reconciliation",
      search: { ...search, ...patch },
      replace: true,
    });

  async function acceptAll() {
    setAccepting(true);
    try {
      for (const [lineId, movement] of suggestions) {
        await match.mutateAsync({
          lineId,
          journalId: movement.journalId,
          journalLineId: movement.journalLineId,
        });
      }
    } finally {
      setAccepting(false);
    }
  }

  const reviewColumns: Array<ColumnDef<StatementLineToReview>> = [
    {
      id: "date",
      header: "Date",
      cell: ({ row: { original: line } }) => formatDate(line.date),
    },
    {
      id: "line",
      header: "Statement line",
      cell: ({ row: { original: line } }) => (
        <span className="flex flex-col">
          <span className="text-fg text-sm">{line.description}</span>
          {line.reference ? (
            <span className="text-fg-muted num text-xs">{line.reference}</span>
          ) : null}
        </span>
      ),
    },
    {
      id: "amount",
      header: "Amount",
      meta: { textAlign: "right", cellClassName: "num" },
      cell: ({ row: { original: line } }) => formatAmount(line.amount),
    },
    {
      id: "match",
      header: "Match in Rigo",
      cell: ({ row: { original: line } }) => {
        const movement = suggestions.get(line.id);
        return movement ? (
          <span className="text-fg text-sm">
            {documentLabel(movement.documentType, movement.number)} · {formatDate(movement.date)}
          </span>
        ) : (
          <span className="text-fg-muted text-sm">No match found</span>
        );
      },
    },
    ...(manages
      ? [
          {
            id: "action",
            header: "",
            cell: ({ row: { original: line } }: { row: { original: StatementLineToReview } }) => {
              const movement = suggestions.get(line.id);
              return (
                <span className="flex justify-end gap-1">
                  {movement ? (
                    <Button
                      colorScheme="primary"
                      variant="outline"
                      size="xs"
                      disabled={match.isPending}
                      aria-label={`Accept the match for ${line.description}`}
                      onClick={() =>
                        match.mutate({
                          lineId: line.id,
                          journalId: movement.journalId,
                          journalLineId: movement.journalLineId,
                        })
                      }
                    >
                      Accept
                    </Button>
                  ) : null}
                  <Button
                    colorScheme="default"
                    variant="ghost"
                    size="xs"
                    aria-label={`Find a match for ${line.description}`}
                    onClick={() => setFinding(line)}
                  >
                    Find
                  </Button>
                  {canCreate ? (
                    <Button
                      colorScheme="default"
                      variant="ghost"
                      size="xs"
                      aria-label={`Create an entry for ${line.description}`}
                      onClick={() => setCreating(line)}
                    >
                      Create
                    </Button>
                  ) : null}
                </span>
              );
            },
          },
        ]
      : []),
  ];

  const matchedLines = matches ? byLine(matches) : undefined;

  const matchedColumns: Array<ColumnDef<MatchedLine>> = [
    {
      id: "date",
      header: "Date",
      cell: ({ row: { original: line } }) => formatDate(line.date),
    },
    {
      id: "line",
      header: "Statement line",
      cell: ({ row: { original: line } }) => line.description,
    },
    {
      id: "amount",
      header: "Amount",
      meta: { textAlign: "right", cellClassName: "num" },
      cell: ({ row: { original: line } }) => formatAmount(line.amount),
    },
    {
      id: "match",
      header: "Matched to",
      cell: ({ row: { original: line } }) => (
        <span className="flex flex-col">
          {line.documents.map((document) => (
            <span key={document}>{document}</span>
          ))}
        </span>
      ),
    },
    ...(manages
      ? [
          {
            id: "action",
            header: "",
            cell: ({ row: { original: line } }: { row: { original: MatchedLine } }) =>
              lockedTo !== undefined && line.date <= lockedTo ? null : (
                <span className="flex justify-end">
                  <Button
                    colorScheme="default"
                    variant="ghost"
                    size="xs"
                    disabled={unmatch.isPending}
                    aria-label={`Undo the match for ${line.description}`}
                    onClick={() => unmatch.mutate(line.matchId)}
                  >
                    Undo
                  </Button>
                </span>
              ),
          },
        ]
      : []),
  ];

  const booksColumns: Array<ColumnDef<LedgerMovement>> = [
    {
      id: "date",
      header: "Date",
      cell: ({ row: { original: movement } }) => formatDate(movement.date),
    },
    {
      id: "document",
      header: "Document",
      cell: ({ row: { original: movement } }) =>
        documentLabel(movement.documentType, movement.number),
    },
    {
      id: "memo",
      header: "Details",
      cell: ({ row: { original: movement } }) => movement.memo ?? "",
    },
    {
      id: "amount",
      header: "Amount",
      meta: { textAlign: "right", cellClassName: "num" },
      cell: ({ row: { original: movement } }) => formatAmount(movement.amount),
    },
  ];

  const difference = data?.difference ?? null;

  return (
    <div className="flex flex-col gap-4">
      <Breadcrumbs
        items={[{ label: "Bank accounts", to: "/banking/accounts" }, { label: "Reconciliation" }]}
      />
      <PageHeader
        title={account ? `Reconcile · ${account.name}` : "Reconciliation"}
        subtitle={account?.number}
        actions={
          <span className="flex flex-wrap gap-2">
            {manages && suggestions.size > 0 ? (
              <Button
                colorScheme="default"
                variant="outline"
                size="sm"
                disabled={accepting}
                onClick={() => void acceptAll()}
              >
                {accepting ? "Accepting" : `Accept all matches (${suggestions.size})`}
              </Button>
            ) : null}
            <PermittedAction
              permission="banking.transfers.create"
              onClick={() => setImporting(true)}
            >
              Import statement
            </PermittedAction>
            {manages && (lockedTo === undefined || asAt > lockedTo) ? (
              <Button
                colorScheme="primary"
                variant="solid"
                size="sm"
                disabled={!data || data.onStatementOnly.length > 0}
                onClick={() => setFinishing(true)}
              >
                Finish reconciliation
              </Button>
            ) : null}
          </span>
        }
      />

      {failure ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={(failure as Error).message}
        />
      ) : null}
      {standing ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-fg text-sm">
            Reconciled to {formatDate(standing.asAt)} · {standing.lineCount}{" "}
            {standing.lineCount === 1 ? "line" : "lines"} · closing balance{" "}
            <span className="num">{formatAmount(standing.statementBalance)}</span>
          </p>
          {manages ? (
            <Button
              colorScheme="default"
              variant="outline"
              size="sm"
              disabled={reopen.isPending}
              onClick={() => reopen.mutate(standing.id)}
            >
              Reopen reconciliation
            </Button>
          ) : null}
        </div>
      ) : null}
      {standing && paisa(standing.ledgerBalance) !== paisa(standing.ledgerBalanceNow) ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "warning" }}
          description={`Something was posted into this account on or before ${formatDate(standing.asAt)} after it was locked. The books said ${formatAmount(standing.ledgerBalance)} then and say ${formatAmount(standing.ledgerBalanceNow)} now.`}
        />
      ) : null}
      {search.problem ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "warning" }}
          description={`The entry is recorded, but it wasn't matched to the line. ${search.problem}`}
        />
      ) : null}

      <div className="grid gap-3 md:grid-cols-3">
        <Select
          label="Account"
          options={(accounts ?? []).map((option) => ({ value: option.id, label: option.name }))}
          value={accountId ?? ""}
          onChange={(details) => set({ account: pickedValue(details) })}
          showClearButton={false}
        />
        <InputText
          label="As at"
          type="date"
          value={asAt}
          onChange={(event) => set({ asAt: event.target.value })}
        />
      </div>

      <KpiStrip
        loading={!data}
        items={
          data
            ? [
                {
                  label: "Statement balance",
                  value:
                    data.statementBalance === null
                      ? "Not stated"
                      : formatAmount(data.statementBalance),
                  detail:
                    data.statementBalance === null
                      ? "No balance in what's been imported"
                      : `Per bank, up to ${formatDate(asAt)}`,
                },
                {
                  label: "Book balance",
                  value: formatAmount(data.ledgerBalance),
                  detail: `The ledger on ${formatDate(asAt)}`,
                },
                {
                  label: "Difference",
                  value: difference === null ? "Unknown" : formatAmount(difference),
                  detail:
                    difference === null
                      ? "Nothing to reconcile to without a statement balance"
                      : Number(difference) === 0
                        ? "Every difference has a name"
                        : "Not explained by the lines below",
                  tone: difference !== null && Number(difference) !== 0 ? "bad" : "default",
                },
                {
                  label: "Lines to review",
                  value: String(data.onStatementOnly.length),
                  detail: `${data.inLedgerOnly.length} in our books only`,
                },
              ]
            : []
        }
      />

      <SegmentControl<Tab>
        label="Reconciliation sections"
        value={tab}
        onChange={setTab}
        segments={[
          { value: "review", label: "To review", badge: data?.onStatementOnly.length },
          { value: "matched", label: "Matched", badge: matchedLines?.length },
          { value: "books", label: "In our books only", badge: data?.inLedgerOnly.length },
        ]}
      />

      {tab === "review" ? (
        <SectionCard title="On the statement, not yet matched" flush>
          <DataTable
            aria-label="Statement lines to review"
            size="sm"
            enableSorting={false}
            columns={reviewColumns}
            data={data?.onStatementOnly ?? []}
            getRowId={(line) => line.id}
            loading={!data}
            emptyContent="Nothing to review. Import a statement to bring in the bank's lines."
            pagination={{ mode: "manual", show: false }}
          />
        </SectionCard>
      ) : null}
      {tab === "matched" ? (
        <SectionCard title="Matched" flush>
          <DataTable
            aria-label="Matched statement lines"
            size="sm"
            enableSorting={false}
            columns={matchedColumns}
            data={matchedLines ?? []}
            getRowId={(line) => line.lineId}
            loading={!matches}
            emptyContent="Nothing has been matched yet."
            pagination={{ mode: "manual", show: false }}
          />
        </SectionCard>
      ) : null}
      {tab === "books" ? (
        <SectionCard title="In our books, not on the statement" flush>
          <DataTable
            aria-label="Movements in our books only"
            size="sm"
            enableSorting={false}
            columns={booksColumns}
            data={data?.inLedgerOnly ?? []}
            getRowId={(movement) => movement.journalLineId}
            loading={!data}
            emptyContent="Everything in the books is on the statement."
            pagination={{ mode: "manual", show: false }}
          />
        </SectionCard>
      ) : null}

      {importing ? (
        <ImportStatementDialog accountId={accountId} onClose={() => setImporting(false)} />
      ) : null}
      {finding ? (
        <FindMatchDialog
          line={finding}
          bankLedgerAccountId={account?.accountId ?? ""}
          onClose={() => setFinding(null)}
          onCreate={
            canCreate
              ? () => {
                  setCreating(finding);
                  setFinding(null);
                }
              : undefined
          }
        />
      ) : null}
      {finishing && data && accountId ? (
        <FinishReconciliationDialog
          accountId={accountId}
          asAt={asAt}
          reconciliation={data}
          onClose={() => setFinishing(false)}
        />
      ) : null}
      {creating && account ? (
        <CreateEntryDialog line={creating} account={account} onClose={() => setCreating(null)} />
      ) : null}
    </div>
  );
}

function FindMatchDialog({
  line,
  bankLedgerAccountId,
  onClose,
  onCreate,
}: {
  line: StatementLineToReview;
  bankLedgerAccountId: string;
  onClose: () => void;
  onCreate?: () => void;
}) {
  const can = useCan();
  const { data: candidates, error } = useMatchCandidates(line.id);
  const { data: accounts } = useAccounts();
  const match = useMatchStatementLineToSeveral();
  const createEntry = useCreateStatementEntry();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [entryAccount, setEntryAccount] = useState("");
  const [entryMemo, setEntryMemo] = useState("");
  const [entryProblem, setEntryProblem] = useState<string | null>(null);

  const chosen = (candidates ?? []).filter((c) => picked.has(c.journalLineId));
  const total = chosen.reduce((sum, c) => sum + paisa(c.amount), 0);
  const left = paisa(line.amount) - total;

  // Built each render, as nothing here is typed into: the tick is the only state in a row.
  const columns: Array<ColumnDef<LedgerMovementCandidate>> = [
    {
      id: "pick",
      header: "",
      size: 40,
      cell: ({ row: { original: candidate } }) => (
        <Checkbox
          aria-label={`Choose ${candidate.number}`}
          checked={picked.has(candidate.journalLineId)}
          onCheckedChange={(details) =>
            setPicked((current) => {
              const next = new Set(current);
              if (details.checked === true) next.add(candidate.journalLineId);
              else next.delete(candidate.journalLineId);
              return next;
            })
          }
        />
      ),
    },
    {
      id: "document",
      header: "Document",
      cell: ({ row: { original: candidate } }) =>
        documentLabel(candidate.documentType, candidate.number),
    },
    {
      id: "date",
      header: "Date",
      cell: ({ row: { original: candidate } }) =>
        `${formatDate(candidate.date)} · ${candidate.daysApart} ${candidate.daysApart === 1 ? "day" : "days"} apart`,
    },
    {
      id: "memo",
      header: "Details",
      cell: ({ row: { original: candidate } }) => candidate.memo ?? "",
    },
    {
      id: "amount",
      header: "Amount",
      meta: { textAlign: "right", cellClassName: "num" },
      cell: ({ row: { original: candidate } }) => formatAmount(candidate.amount),
    },
  ];

  const movements = chosen.map((c) => ({ journalId: c.journalId, journalLineId: c.journalLineId }));
  // Bank charges or tax can leave a line short of the records: an entry takes up what's left (ADR-0051).
  const difference = chosen.length > 0 && left !== 0;
  const canEnter = difference && can("accounting.journals.create");
  const failure = match.error ?? createEntry.error ?? error;

  if (entryProblem) {
    return (
      <Dialog open onClose={onClose} title="Entry posted" dismissLabel="Done">
        <Alert
          config={{ variant: "subtle", colorScheme: "warning" }}
          description={`The entry is posted, but it wasn't matched to the line. ${entryProblem}`}
        />
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title="Find a match"
      description={`${line.description} · ${formatAmount(line.amount)} on ${formatDate(line.date)}`}
      footer={
        canEnter ? (
          <Button
            colorScheme="primary"
            variant="solid"
            size="sm"
            disabled={entryAccount === "" || createEntry.isPending}
            onClick={() =>
              createEntry.mutate(
                {
                  lineId: line.id,
                  accountId: entryAccount,
                  memo: entryMemo.trim() || `Difference on ${line.description}`,
                  movements,
                },
                {
                  onSuccess: ({ matchProblem }) => {
                    if (matchProblem) setEntryProblem(matchProblem);
                    else onClose();
                  },
                },
              )
            }
          >
            {createEntry.isPending ? "Matching" : "Match with an entry for the difference"}
          </Button>
        ) : (
          <Button
            colorScheme="primary"
            variant="solid"
            size="sm"
            disabled={chosen.length === 0 || left !== 0 || match.isPending}
            onClick={() => match.mutate({ lineId: line.id, movements }, { onSuccess: onClose })}
          >
            {match.isPending ? "Matching" : "Match"}
          </Button>
        )
      }
    >
      <div className="flex flex-col gap-3">
        {failure ? (
          <Alert
            config={{ variant: "subtle", colorScheme: "danger" }}
            description={(failure as Error).message}
          />
        ) : null}
        <DataTable
          aria-label="Possible matches"
          size="sm"
          enableSorting={false}
          columns={columns}
          data={candidates ?? []}
          getRowId={(candidate) => candidate.journalLineId}
          loading={candidates === undefined}
          emptyContent="Nothing unmatched in the books within 30 days."
          pagination={{ mode: "manual", show: false }}
        />
        {chosen.length > 0 ? (
          <p className={`text-xs ${left === 0 ? "text-fg-muted" : "text-warn-text"}`}>
            Chosen <span className="num">{formatAmount((total / 100).toFixed(2))}</span> of{" "}
            <span className="num">{formatAmount(line.amount)}</span>.{" "}
            {left === 0
              ? "That matches the line."
              : `The total has to match the line exactly: ${formatAmount((left / 100).toFixed(2))} still differs. Choose other records, or add an entry for the difference, such as a bank charge.`}
          </p>
        ) : null}
        {canEnter ? (
          <div className="grid gap-3 md:grid-cols-2">
            <Select
              label="Account for the difference"
              required
              options={entryAccountOptions(accounts, bankLedgerAccountId, left > 0)}
              value={entryAccount}
              showClearButton={false}
              onChange={(details) => setEntryAccount(pickedValue(details))}
            />
            <InputText
              label="Memo"
              placeholder={`Difference on ${line.description}`}
              value={entryMemo}
              onChange={(event) => setEntryMemo(event.target.value)}
            />
          </div>
        ) : null}
        {onCreate ? (
          <span>
            <Button colorScheme="primary" variant="ghost" size="sm" onClick={onCreate}>
              Nothing fits? Create an entry
            </Button>
          </span>
        ) : null}
      </div>
    </Dialog>
  );
}

type LedgerMovementCandidate = LedgerMovement & { daysApart: number };
